import React, { useEffect, useRef, useState } from "react";

import { count, sampleText } from "../../campaignUtils";
import flashLogo from "assets/img/flash_color_logo_text@3x.png";

import { Action, Alert, Field } from "../../shared/StudioControls";

const CAPTION = {
  push: "Push notification",
  sms: "SMS",
  email: "Email · Outlook"
};

const clockTime = () => new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
const displayDate = value => value ? new Date(`${value}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }) : new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

// Flash Health app icon: white bolt on the brand red.
const AppIcon = ({ className }) => (
  <span className={className} aria-hidden="true">
    <svg viewBox="0 0 24 24"><path fill="#fff" d="M13.6 2.4 5.3 12.1c-.5.6-.1 1.4.6 1.4h4.6l-1.9 7.3c-.2.8.8 1.3 1.3.7l8.3-9.7c.5-.6.1-1.4-.6-1.4H13l1.9-7.3c.2-.8-.8-1.3-1.3-.7Z" /></svg>
  </span>
);

// Links render the way phones and mail apps show them.
const Linkified = ({ text }) => String(text || "").split(/(https?:\/\/\S+)/g).map((part, index) =>
  /^https?:\/\//.test(part) ? <span key={index} className="cs-mock-link">{part}</span> : <React.Fragment key={index}>{part}</React.Fragment>
);

// iPhone 14 Pro hardware details: Dynamic Island and home indicator.
const PhoneChrome = ({ light }) => (
  <React.Fragment>
    <span className="cs-phone__island" aria-hidden="true" />
    <span className={`cs-phone__home ${light ? "is-light" : ""}`} aria-hidden="true" />
  </React.Fragment>
);

const StatusBar = ({ light }) => (
  <div className={`cs-phone__status ${light ? "is-light" : ""}`} aria-hidden="true">
    <span>{clockTime()}</span>
    <span className="cs-phone__signal">
      <span className="cs-phone__bars"><i /><i /><i /><i /></span>
      4G
      <span className="cs-phone__battery"><i /></span>
    </span>
  </div>
);

// The notification card itself, shared by the inline preview and the phone view.
const PushCard = ({ subject, body }) => (
  <div className="cs-push">
    <AppIcon className="cs-app-icon" />
    <div className="cs-push__content">
      <div className="cs-push__row">
        <span className="cs-push__title">{subject || "Flash Health"}</span>
        <span className="cs-push__when">now</span>
      </div>
      <p className="cs-push__body">{body}</p>
    </div>
  </div>
);

const SmsThread = ({ body }) => (
  <div className="cs-sms__thread">
    <span className="cs-sms__stamp">Text Message · Today {clockTime()}</span>
    <p className="cs-sms__bubble"><Linkified text={body} /></p>
  </div>
);

const PushMock = ({ subject, body, date }) => (
  <div className="cs-phone cs-phone--lock">
    <PhoneChrome light />
    <StatusBar light />
    <div className="cs-lock__clock" aria-hidden="true">
      <span className="cs-lock__date">{displayDate(date)}</span>
      <span className="cs-lock__time">{clockTime()}</span>
    </div>
    <PushCard subject={subject} body={body} />
    <p className="cs-mock-note">iOS cuts long text after about four lines on the lock screen.</p>
  </div>
);

const SmsMock = ({ body }) => (
  <div className="cs-phone cs-phone--sms">
    <PhoneChrome />
    <StatusBar />
    <div className="cs-sms__header">
      <span className="cs-sms__avatar" aria-hidden="true">FH</span>
      <span className="cs-sms__sender">FlashHealth</span>
    </div>
    <SmsThread body={body} />
  </div>
);

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="m21 3-7 7" /><path d="m3 21 7-7" />
  </svg>
);

// Full iPhone 14 Pro view over the page. Closes on Escape, the backdrop or ×.
const PhoneOverlay = ({ label, onClose, children }) => {
  const closeRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    if (closeRef.current) closeRef.current.focus();
    const handleKey = event => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      if (previous && previous.focus) previous.focus();
    };
  }, [onClose]);

  return (
    <div className="cs-phone-overlay" role="dialog" aria-modal="true" aria-label={label} onClick={onClose}>
      <div className="cs-phone-overlay__inner" onClick={event => event.stopPropagation()}>
        <button ref={closeRef} type="button" className="cs-phone-overlay__close" aria-label="Close phone view" onClick={onClose}>×</button>
        {children}
      </div>
    </div>
  );
};

// Inline: only the message itself. Clicking it (or the button) opens the phone view.
const MessageStage = ({ channel, subject, body, date }) => {
  const [open, setOpen] = useState(false);
  const label = channel === "push" ? "Push notification on iPhone" : "SMS on iPhone";
  return (
    <React.Fragment>
      <div className={`cs-msg-stage cs-msg-stage--${channel}`} onClick={() => setOpen(true)}>
        {channel === "push" ? (
          <PushCard subject={subject} body={body} />
        ) : (
          <React.Fragment>
            <div className="cs-msg-stage__sender">
              <span className="cs-sms__avatar" aria-hidden="true">FH</span>
              <span>FlashHealth</span>
            </div>
            <SmsThread body={body} />
          </React.Fragment>
        )}
        <button
          type="button"
          className="cs-msg-stage__expand"
          onClick={event => { event.stopPropagation(); setOpen(true); }}
        >
          <ExpandIcon />
          View on phone
        </button>
      </div>
      {open && (
        <PhoneOverlay label={label} onClose={() => setOpen(false)}>
          {channel === "push" ? <PushMock subject={subject} body={body} date={date} /> : <SmsMock body={body} />}
        </PhoneOverlay>
      )}
    </React.Fragment>
  );
};

const outlookDate = value => {
  const now = value ? new Date(`${value}T12:00:00`) : new Date();
  const day = now.toLocaleDateString("en-GB", { weekday: "short" });
  return `${day} ${now.toLocaleDateString("en-GB")} ${clockTime()}`;
};

const MailIcon = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const ReplyIcon = () => <MailIcon><path d="M9 15 4 10l5-5" /><path d="M4 10h9a7 7 0 0 1 7 7v2" /></MailIcon>;
const ReplyAllIcon = () => <MailIcon><path d="M8 15 3 10l5-5" /><path d="M12 15 7 10l5-5" /><path d="M7 10h7a7 7 0 0 1 7 7v2" /></MailIcon>;
const ForwardIcon = () => <MailIcon><path d="m15 15 5-5-5-5" /><path d="M20 10h-9a7 7 0 0 0-7 7v2" /></MailIcon>;

// Mirrors the backend template (resources/views/mail/aiCampaign.blade.php):
// logo, subject heading, one campaign image, body, red brand button, dark footer.
const EmailTemplate = ({ subject, body, banner, bannerAlt }) => (
  <div className="cs-mailtpl">
    <div className="cs-mailtpl__logo"><img src={flashLogo} alt="Flash Health" /></div>
    <p className="cs-mailtpl__heading">{subject}</p>
    {banner && <img className="cs-mailtpl__banner" src={banner} alt={bannerAlt} />}
    <div className="cs-mailtpl__content">
      <p className="cs-mailtpl__body"><Linkified text={body} /></p>
      <span className="cs-mailtpl__button">Flash Health</span>
    </div>
    <div className="cs-mailtpl__footer">Flash Health</div>
  </div>
);

// Outlook reading pane around the email.
const EmailMock = ({ subject, body, banner, bannerAlt, html, htmlTitle, date }) => (
  <div className="cs-outlook">
    <p className="cs-outlook__subject">{subject || "(No subject)"}</p>
    <div className="cs-outlook__message">
      <div className="cs-outlook__header">
        <span className="cs-outlook__avatar" aria-hidden="true">FH</span>
        <div className="cs-outlook__who">
          <span>Flash Health&lt;noreply@flash.health&gt;</span>
          <span>To: You</span>
        </div>
        <div className="cs-outlook__meta" aria-hidden="true">
          <span className="cs-outlook__icons">
            <span className="is-reply"><ReplyIcon /></span>
            <span className="is-reply"><ReplyAllIcon /></span>
            <span className="is-forward"><ForwardIcon /></span>
            <span className="cs-outlook__more">···</span>
          </span>
          <span className="cs-outlook__date">{outlookDate(date)}</span>
        </div>
      </div>
      <div className="cs-outlook__canvas">
        {html
          ? <iframe title={htmlTitle} sandbox="" referrerPolicy="no-referrer" srcDoc={html} />
          : <EmailTemplate subject={subject} body={body} banner={banner} bannerAlt={bannerAlt} />}
      </div>
      <div className="cs-outlook__actions" aria-hidden="true">
        <span className="is-reply"><ReplyIcon /> Reply</span>
        <span className="is-forward"><ForwardIcon /> Forward</span>
      </div>
    </div>
  </div>
);

export const ContentPreview = ({ variant, channel, stage, stageIndex, language, banners, preview, lead, audience, canUseSaved, isGenerating, isBusy, onLeadChange, onPreview, previewDate, showServerControls = true }) => {
  const rendered = preview && preview.previews.find(item =>
    item.stage === stageIndex && item.channel === channel && item.language === language);
  const renderedVariant = rendered && rendered.variant;
  const subject = renderedVariant ? renderedVariant.subject : sampleText(variant.subject);
  const body = renderedVariant ? renderedVariant.body : sampleText(variant.body);

  return (
    <aside className="cs-preview" aria-label="Message preview">
      <div className="cs-preview__label">
        <span>{CAPTION[channel]}</span>
        <span className="cs-muted">{rendered ? `Server preview · revision ${preview.revision}` : "Sample name"}</span>
      </div>

      {channel !== "email" && <MessageStage channel={channel} subject={subject} body={body} date={previewDate} />}
      {channel === "email" && (
        <EmailMock
          subject={subject}
          body={body}
          banner={banners && banners[stageIndex]}
          bannerAlt={`${stage} email banner`}
          html={rendered && rendered.html}
          htmlTitle={`${stage} email preview`}
          date={previewDate}
        />
      )}
      {channel === "email" && isGenerating && !(banners && banners[stageIndex]) && (
        <p className="cs-muted">The {stage} banner is generated after all messages · it appears here when ready</p>
      )}
      {renderedVariant && channel !== "email" && <small className="cs-muted">{count(renderedVariant.body)} final characters</small>}

      {rendered && rendered.error && <Alert tone="danger">{rendered.error}</Alert>}
      {preview && !rendered && (
        <p className="cs-muted">
          Server preview covers this customer’s language only · review {language} in the editor
        </p>
      )}

      {showServerControls && <div className="cs-preview__server">
        <Field
          label="Preview for"
          hint={canUseSaved ? undefined : isGenerating ? "Available when generation finishes" : "Save your edits first"}
          help="Loads the saved revision with a real customer’s details"
        >
          <select value={lead} disabled={!canUseSaved} onChange={event => onLeadChange(event.target.value)}>
            <option value="">Sample customer</option>
            {audience && audience.data.map(member => (
              <option key={member.id} value={member.id}>{member.name} · {member.id}</option>
            ))}
          </select>
        </Field>
        <Action disabled={!canUseSaved || isBusy} onClick={onPreview}>Load server preview</Action>
      </div>}
    </aside>
  );
};
